
import { useState } from 'react';

const HomePage = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    message: ''
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate textarea length
    if (formData.message.length > 500) {
      alert('ご質問・ご要望は500文字以内で入力してください。');
      return;
    }

    try {
      const response = await fetch('https://readdy.ai/api/form/d45l30fgaed3g6l522n0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          name: formData.name,
          phone: formData.phone,
          email: formData.email,
          message: formData.message
        })
      });

      if (response.ok) {
        alert('予約申し込みありがとうございます。担当者より折り返しご連絡いたします。');
        setFormData({ name: '', phone: '', email: '', message: '' });
      } else {
        alert('送信に失敗しました。もう一度お試しください。');
      }
    } catch (error) {
      alert('送信に失敗しました。もう一度お試しください。');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-pink-50 to-purple-50">
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/8e6ebf8de9dac50492cf2bd593fb8077.jpg"
            alt="スモールショップ外観"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/30 to-transparent"></div>
        </div>
        
        <div className="relative px-6 pt-16 pb-20 text-center">
          <h1 className="text-3xl font-bold text-white mb-2" style={{ fontFamily: 'Pacifico, serif' }}>
            logo
          </h1>
          <h2 className="text-2xl font-bold text-white mb-4 leading-tight">
            「私らしい働き方」を叶える<br />
            「スモールショップ」
          </h2>
          <p className="text-white/90 mb-8 text-sm leading-relaxed">
            夢を形にしたいママ起業家のための<br />
            小さなコンテナ店舗
          </p>
          
          <div className="space-y-3">
            <button className="w-full bg-gradient-to-r from-green-500 to-green-600 text-white py-4 px-6 rounded-full font-semibold shadow-lg">
              <i className="ri-line-fill mr-2"></i>
              LINE登録
            </button>
            <button 
              onClick={() => document.getElementById('reservation-form')?.scrollIntoView({ behavior: 'smooth' })}
              className="w-full bg-gradient-to-r from-pink-500 to-rose-500 text-white py-4 px-6 rounded-full font-semibold shadow-lg"
            >
              <i className="ri-calendar-line mr-2"></i>
              モデルハウス見学予約
            </button>
          </div>
        </div>
      </section>

      {/* Concept Section */}
      <section className="px-6 py-12 bg-white">
        <div className="text-center mb-8">
          <h3 className="text-2xl font-bold text-gray-800 mb-4">コンセプト</h3>
          <p className="text-gray-600 leading-relaxed">
            「スモールショップ」は、夢を形にしたいママ起業家のための小さなコンテナ店舗。<br />
            工場製作で高品質、設置は最短1日。<br />
            9坪に必要な設備が揃い、低コストで自分のお店を実現できます。<br />
            「私らしい働き方」を叶える、新しいママ起業家のカタチです。
          </p>
        </div>
      </section>

      {/* Model House Section */}
      <section className="px-6 py-12 bg-gradient-to-r from-purple-50 to-pink-50">
        <h3 className="text-2xl font-bold text-center text-gray-800 mb-8">モデルハウス</h3>
        <div className="rounded-2xl overflow-hidden shadow-lg mb-6">
          <img 
            src="https://readdy.ai/api/search-image?query=Interior%20view%20of%20small%20modern%20container%20shop%2C%20cozy%20cafe%20or%20retail%20space%2C%20wooden%20flooring%2C%20white%20walls%2C%20natural%20lighting%2C%20minimal%20furniture%2C%20clean%20design%2C%20perfect%20for%20female%20entrepreneurs%2C%20warm%20atmosphere%2C%20professional%20interior%20photography&width=375&height=250&seq=modelhouse1&orientation=landscape"
            alt="モデルハウス内装"
            className="w-full h-64 object-cover object-top"
          />
        </div>
      </section>

      {/* Specifications Section */}
      <section className="px-6 py-12 bg-white">
        <h3 className="text-2xl font-bold text-center text-gray-800 mb-8">住宅仕様</h3>
        
        <div className="bg-gradient-to-br from-pink-50 to-purple-50 rounded-2xl p-6 mb-6">
          <div className="mb-6">
            <h4 className="text-lg font-bold text-pink-600 mb-3">商品名：S</h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="font-semibold text-gray-700">用途：</span>
                <span className="text-gray-600">コンテナ店舗</span>
              </div>
              <div>
                <span className="font-semibold text-gray-700">面積：</span>
                <span className="text-gray-600">約9坪</span>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              サイズ：2640mm×8,190mm、H2,400mm<br />
              ※確認申請が必要となります
            </p>
          </div>

          <div className="space-y-4">
            <div>
              <h5 className="font-semibold text-gray-700 mb-2">外装仕様</h5>
              <div className="grid grid-cols-2 gap-2 text-sm text-gray-600">
                <div>屋根：ガルバリウム積層板</div>
                <div>外壁：金属サイディング仕上げ</div>
              </div>
            </div>

            <div>
              <h5 className="font-semibold text-gray-700 mb-2">内装仕様</h5>
              <div className="grid grid-cols-1 gap-1 text-sm text-gray-600">
                <div>天井：化粧ボード仕上げ</div>
                <div>壁：化粧ボード仕上げ</div>
                <div>床：フローリング柄塩ビタイル仕上げ</div>
              </div>
            </div>

            <div>
              <h5 className="font-semibold text-gray-700 mb-2">設備</h5>
              <div className="grid grid-cols-1 gap-1 text-sm text-gray-600">
                <div>電気設備：コンセント、一部照明あり</div>
                <div>給排水設備：ミニキッチン、水洗トイレ</div>
              </div>
            </div>
          </div>

          <div className="mt-6 p-4 bg-white/70 rounded-lg">
            <p className="text-xs text-gray-600 leading-relaxed">
              <span className="font-semibold">別途諸費用：</span>
              基礎工事、給排水工事、本線電気工事、ガス工事、確認申請、改修構成費用は別途お見積りとなります。<br />
              ※その他詳細につきましては、お問い合わせ下さい。
            </p>
          </div>
        </div>
      </section>

      {/* Floor Plan Section */}
      <section className="px-6 py-12 bg-gradient-to-r from-purple-50 to-pink-50">
        <h3 className="text-2xl font-bold text-center text-gray-800 mb-8">間取り図</h3>
        <div className="rounded-2xl overflow-hidden shadow-lg">
          <img 
            src="https://readdy.ai/api/search-image?query=Simple%20floor%20plan%20layout%20of%20small%209%20tsubo%20container%20shop%2C%20architectural%20drawing%20style%2C%20clean%20white%20background%2C%20detailed%20room%20layout%20with%20kitchen%20area%20and%20toilet%2C%20professional%20blueprint%20design%2C%20easy%20to%20understand%20layout&width=375&height=250&seq=floorplan1&orientation=landscape"
            alt="間取り図"
            className="w-full h-64 object-cover object-top"
          />
        </div>
      </section>

      {/* Recommendation Section */}
      <section className="px-6 py-12 bg-white">
        <h3 className="text-2xl font-bold text-center text-gray-800 mb-8">こんな方におススメ</h3>
        
        <div className="space-y-4">
          <div className="flex items-start p-4 bg-gradient-to-r from-pink-50 to-purple-50 rounded-xl">
            <div className="w-8 h-8 bg-pink-500 rounded-full flex items-center justify-center mr-4 mt-1 flex-shrink-0">
              <i className="ri-money-dollar-circle-line text-white text-sm"></i>
            </div>
            <p className="text-gray-700 text-sm leading-relaxed">
              初期費用を抑えて自宅で小さなお店を始めたい方
            </p>
          </div>
          
          <div className="flex items-start p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl">
            <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center mr-4 mt-1 flex-shrink-0">
              <i className="ri-time-line text-white text-sm"></i>
            </div>
            <p className="text-gray-700 text-sm leading-relaxed">
              最短1日で設置できるスピード開業で、すぐに夢を形にしたい方
            </p>
          </div>
          
          <div className="flex items-start p-4 bg-gradient-to-r from-pink-50 to-purple-50 rounded-xl">
            <div className="w-8 h-8 bg-rose-500 rounded-full flex items-center justify-center mr-4 mt-1 flex-shrink-0">
              <i className="ri-heart-3-line text-white text-sm"></i>
            </div>
            <p className="text-gray-700 text-sm leading-relaxed">
              「私らしく働きたい」そんな想いを持つママ起業家
            </p>
          </div>
        </div>
      </section>

      {/* Customer Reviews Section */}
      <section className="px-6 py-12 bg-gradient-to-r from-purple-50 to-pink-50">
        <h3 className="text-2xl font-bold text-center text-gray-800 mb-8">お客様の声</h3>
        
        <div className="space-y-6">
          {/* Review 1 */}
          <div className="bg-white rounded-2xl p-6 shadow-md">
            <div className="flex items-center mb-4">
              <div className="w-12 h-12 bg-gradient-to-br from-pink-400 to-purple-400 rounded-full flex items-center justify-center mr-4">
                <i className="ri-user-smile-line text-white text-lg"></i>
              </div>
              <div>
                <h4 className="font-semibold text-gray-800">40代・エステサロン経営</h4>
                <p className="text-sm text-gray-500">秋田市在住</p>
              </div>
            </div>
            <p className="text-gray-700 text-sm leading-relaxed">
              テナントを借りていた頃は家賃の負担が大きく、いつか自分のお店を持ちたいと思っていました。
              スモールショップなら、自宅の敷地を使って低コストで理想のサロン空間を実現できました。
              設置もあっという間で、わずか1日でオープン準備完了。
              今はお客様と一対一で落ち着いて施術できる時間がとても幸せです。
            </p>
          </div>

          {/* Review 2 */}
          <div className="bg-white rounded-2xl p-6 shadow-md">
            <div className="flex items-center mb-4">
              <div className="w-12 h-12 bg-gradient-to-br from-green-400 to-blue-400 rounded-full flex items-center justify-center mr-4">
                <i className="ri-user-smile-line text-white text-lg"></i>
              </div>
              <div>
                <h4 className="font-semibold text-gray-800">30代・ヨガインストラクター</h4>
                <p className="text-sm text-gray-500">秋田市在住</p>
              </div>
            </div>
            <p className="text-gray-700 text-sm leading-relaxed">
              レンタルスペースを転々としていましたが、自分の拠点が欲しいと思っていました。
              スモールショップは9坪でも十分広く、光が入る明るい空間にできたのが嬉しいです。
              将来は別の土地に移すこともできると聞いて、無理のない形で独立できました。
              生徒さんも「ここ、落ち着くね」って言ってくれます。
            </p>
          </div>

          {/* Review 3 */}
          <div className="bg-white rounded-2xl p-6 shadow-md">
            <div className="flex items-center mb-4">
              <div className="w-12 h-12 bg-gradient-to-br from-yellow-400 to-orange-400 rounded-full flex items-center justify-center mr-4">
                <i className="ri-user-smile-line text-white text-lg"></i>
              </div>
              <div>
                <h4 className="font-semibold text-gray-800">30代・雑貨＆お菓子ショップ運営</h4>
                <p className="text-sm text-gray-500">横手市在住</p>
              </div>
            </div>
            <p className="text-gray-700 text-sm leading-relaxed">
              趣味で作っていたハンドメイド雑貨と焼き菓子を販売したくて、思い立って独立。
              スモールショップなら小さく始められてリスクも少なく、自分のペースでお店を運営できます。
              私の「好き」をそのまま形にできたのが一番の喜びです。
            </p>
          </div>
        </div>
      </section>

      {/* Reservation Form Section */}
      <section id="reservation-form" className="px-6 py-12 bg-white">
        <h3 className="text-2xl font-bold text-center text-gray-800 mb-8">モデルハウス見学予約フォーム</h3>
        
        <form onSubmit={handleSubmit} data-readdy-form className="space-y-6">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">お名前 *</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="山田 花子"
              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-pink-400 focus:border-transparent text-sm"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">電話番号 *</label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              placeholder="090-1234-5678"
              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-pink-400 focus:border-transparent text-sm"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">メールアドレス *</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="example@email.com"
              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-pink-400 focus:border-transparent text-sm"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">ご質問・ご要望</label>
            <textarea
              name="message"
              value={formData.message}
              onChange={handleInputChange}
              placeholder="見学希望日時や、ご質問などがございましたらお気軽にお書きください"
              rows={4}
              maxLength={500}
              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-pink-400 focus:border-transparent text-sm resize-none"
            />
            <p className="text-xs text-gray-500 mt-1">{formData.message.length}/500文字</p>
          </div>

          <div className="text-center">
            <a 
              href="/privacy-policy" 
              className="text-sm text-blue-600 underline hover:text-blue-800"
            >
              個人情報の取り扱いについて
            </a>
          </div>

          <button
            type="submit"
            className="w-full bg-gradient-to-r from-pink-500 to-rose-500 text-white py-4 px-6 rounded-full font-semibold shadow-lg hover:shadow-xl transition-shadow"
          >
            <i className="ri-calendar-line mr-2"></i>
            モデルハウス予約
          </button>
        </form>
      </section>

      {/* Company Info Section */}
      <section className="px-6 py-12 bg-gradient-to-r from-purple-50 to-pink-50">
        <h3 className="text-2xl font-bold text-center text-gray-800 mb-8">会社紹介</h3>
        
        <div className="bg-white rounded-2xl p-6 shadow-md">
          <div className="mb-6">
            <h4 className="text-lg font-bold text-pink-600 mb-4">湯沢開発株式会社</h4>
            <div className="space-y-2 text-sm">
              <div className="flex">
                <span className="font-semibold text-gray-700 w-20">代表者：</span>
                <span className="text-gray-600">高橋 正明</span>
              </div>
              <div className="flex">
                <span className="font-semibold text-gray-700 w-20">所在地：</span>
                <span className="text-gray-600">〒012-0844 秋田県湯沢市田町2丁目1番3号</span>
              </div>
              <div className="flex">
                <span className="font-semibold text-gray-700 w-20">電話番号：</span>
                <span className="text-gray-600">0183-73-7125</span>
              </div>
              <div className="flex">
                <span className="font-semibold text-gray-700 w-20">営業時間：</span>
                <span className="text-gray-600">8:45～17:00</span>
              </div>
              <div className="flex">
                <span className="font-semibold text-gray-700 w-20">定休日：</span>
                <span className="text-gray-600">水曜日</span>
              </div>
              <div className="flex">
                <span className="font-semibold text-gray-700 w-20">設立：</span>
                <span className="text-gray-600">1964年11月（創業1951年）</span>
              </div>
            </div>
          </div>

          <div className="mb-6">
            <h5 className="font-semibold text-gray-700 mb-2">許可・登録</h5>
            <div className="text-xs text-gray-600 space-y-1">
              <div>宅地建物取引業免許：秋田県知事免許(17)第43号</div>
              <div>建設業登録：秋田県知事許可（般-4）第80084号</div>
              <div>一級建築士事務所：秋田県知事登録 第16-10A-0250号</div>
            </div>
          </div>

          <div className="mb-6">
            <h5 className="font-semibold text-gray-700 mb-2">参加団体</h5>
            <div className="text-xs text-gray-600 space-y-1">
              <div>（公社）秋田県宅地建物取引業協会</div>
              <div>（社）全国宅地建物取引業保証協会</div>
              <div>(財）東日本不動産流通機構</div>
              <div>(財）住宅保証機構 日本住宅保証検査機構</div>
            </div>
          </div>

          <div>
            <h5 className="font-semibold text-gray-700 mb-2">有資格者</h5>
            <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
              <div>一級建築士（1名）</div>
              <div>二級建築士（1名）</div>
              <div>宅地建物取引主任者（7名）</div>
              <div>公認不動産コンサルティングマスター（1名）</div>
              <div>賃貸不動産経営管理士（1名）</div>
              <div>住宅ローンアドバイザー（2名）</div>
              <div>ファイナンシャルプランナー（1名）</div>
            </div>
          </div>

          <div className="mt-6 p-4 bg-gradient-to-r from-pink-50 to-purple-50 rounded-lg text-center">
            <p className="text-sm font-semibold text-pink-600">
              1951年創業、75年以上の実績
            </p>
            <p className="text-xs text-gray-600 mt-1">
              瑕疵保証保険適用・建設業許可・確認申請対応済みで安心
            </p>
          </div>
        </div>
      </section>

      {/* Access Section */}
      <section className="px-6 py-12 bg-white">
        <h3 className="text-2xl font-bold text-center text-gray-800 mb-8">アクセス</h3>
        
        <div className="rounded-2xl overflow-hidden shadow-lg">
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3048.8234567890123!2d140.48926731234567!3d39.16066671234567!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x5f8a9b1234567890%3A0x1234567890abcdef!2z56eL55Sw55yM5rmv5rKi5biC55Sw55S677yS5LiB55uu77yR4oiS77yT!5e0!3m2!1sja!2sjp!4v1234567890123!5m2!1sja!2sjp"
            width="100%"
            height="300"
            style={{ border: 0 }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="湯沢開発株式会社へのアクセス"
          />
        </div>
        
        <div className="mt-4 text-center">
          <p className="text-sm text-gray-600">
            〒012-0844 秋田県湯沢市田町2丁目1番3号
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-800 text-white py-8 px-6">
        <div className="text-center">
          <p className="text-sm">Copyright© 2025</p>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
